import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

type ValidateCustomerPartIdParams = {
  customerPartId: string;
  quoteLineUuid: string;
};

const validator = z.coerce.string();

const configuredPartValidator = validator.nonempty(
  'Customer Part ID is required for newly configured parts.',
);

export function validateCustomerPartId(
  state: WritableQuoteBuilderStateType,
  { customerPartId, quoteLineUuid }: ValidateCustomerPartIdParams,
) {
  const customerPartIdString = customerPartId.toString();

  const invalidIssueId = buildIssueId({
    area: 'quoteLine',
    quoteLineUuid,
    type: 'invalid',
    field: 'customerPartId',
  });

  const isConfiguredPart = Boolean(
    state.quoteLines[quoteLineUuid].configuratorSnapshot,
  );

  const result = isConfiguredPart
    ? configuredPartValidator.safeParse(customerPartIdString)
    : validator.safeParse(customerPartIdString);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    upsertIssue(state, invalidIssueId, {
      title: 'Invalid customer part ID',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'customerPartId',
      area: 'quoteLine',
      quoteLineUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quoteLineUuid,
          field: 'customerPartId',
        }),
      },
    });
    return customerPartIdString;
  }

  deleteIssue(state, invalidIssueId);

  return result.data;
}
